import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useStudents } from 'context/StudentsContext';
import { useIncidents } from 'context/IncidentsContext';

// ---------------------------------------------------------------------------
// Student Progress Tracker  (route: /progress-tracker)
//
// A single caseload-wide overview of every student × active goal, with
// enough at-a-glance signal that a teacher can walk into the room in the
// morning, open this page, and instantly answer:
//
//   * Which goals have I not collected data on in over two weeks?
//     (the "needs attention" column)
//   * Which of my students are close to mastery so I can front-load an
//     IEP review meeting?
//   * Where has behavior data been linked to a goal, i.e. we're already
//     capturing evidence I don't need to schedule extra probes for?
//
// The design keeps the primary action ("+ Log data") one tap away on
// every row — she never has to remember which page the goal-progress
// form lives on. Sorting defaults to "Needs attention" so stale-data
// goals bubble to the top; that's the single biggest FBA/BIP audit
// finding at IEP review time, so making it hard to miss is the whole
// point of this page.
// ---------------------------------------------------------------------------

const STALE_THRESHOLD_DAYS = 14;
const NEAR_MASTERY_THRESHOLD = 80;

const DOMAIN_LABELS = {
  academic:            'Academic',
  behavioral:          'Behavioral',
  'social-emotional':  'Social / Emotional',
  communication:       'Communication',
  functional:          'Functional',
  motor:               'Motor',
  transition:          'Transition',
  other:               'Other',
};

const DOMAIN_ICONS = {
  academic:            'BookOpen',
  behavioral:          'Users',
  'social-emotional':  'Heart',
  communication:       'MessageCircle',
  functional:          'Wrench',
  motor:               'Activity',
  transition:          'Compass',
  other:               'Target',
};

const STATUS_LABEL = {
  not_started:  'Not started',
  in_progress:  'In progress',
  mastered:     'Mastered',
  discontinued: 'Discontinued',
};

// Days elapsed since `iso` (goal.lastUpdated). Returns null if the goal
// has never been touched — that itself is a "needs attention" signal.
function daysSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  return days;
}

function formatDaysAgo(days) {
  if (days === null || days === undefined) return 'No data yet';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return `${Math.floor(days / 7)} week ago`;
  return `${Math.floor(days / 7)} weeks ago`;
}

// Compose a student's display name. Prefer "Sam Perez" full-form on this
// page because the table has room; fall back to "Unnamed" so a partially-
// filled roster still renders every row.
function displayName(student) {
  if (!student) return 'Unnamed student';
  const first = student.firstName || '';
  const last = student.lastName || '';
  const joined = `${first} ${last}`.trim();
  return joined || 'Unnamed student';
}

const StudentProgressTracker = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { students, loading: studentsLoading } = useStudents();
  const { goals, loading: goalsLoading } = useIEPGoals();
  const { incidents } = useIncidents();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    domain: 'all',
    sortBy: 'needs_attention',
    showMastered: false,
  });

  // studentId -> student (for quick lookups)
  const studentById = useMemo(() => {
    const map = new Map();
    (Array.isArray(students) ? students : []).forEach((s) => map.set(s.id, s));
    return map;
  }, [students]);

  // goalId -> count of linked behavior incidents. This is the "we're
  // already capturing evidence" column that lets a teacher trust the
  // number instead of double-collecting.
  const linkedIncidentCountByGoal = useMemo(() => {
    const counts = new Map();
    (Array.isArray(incidents) ? incidents : []).forEach((inc) => {
      const linked = Array.isArray(inc.linkedGoalIds) ? inc.linkedGoalIds : [];
      linked.forEach((goalId) => {
        counts.set(goalId, (counts.get(goalId) || 0) + 1);
      });
    });
    return counts;
  }, [incidents]);

  // Compose a row for each (student × goal) pair. Rows carry every
  // derived signal the table renders so the sort/filter logic below
  // stays simple.
  const rows = useMemo(() => {
    const list = Array.isArray(goals) ? goals : [];
    return list.map((goal) => {
      const student = studentById.get(goal.studentId);
      const days = daysSince(goal.lastUpdated || goal.createdAt);
      const progress = Number.isFinite(goal.progressPercent)
        ? goal.progressPercent
        : null;

      const isMastered = goal.status === 'mastered';
      const isDiscontinued = goal.status === 'discontinued';
      const isActive = !isMastered && !isDiscontinued;

      // "Needs attention" = active goal with no updates for more than
      // STALE_THRESHOLD_DAYS. Goals that were never touched at all also
      // count.
      const needsAttention =
        isActive && (days === null || days > STALE_THRESHOLD_DAYS);
      const nearMastery =
        isActive && progress !== null && progress >= NEAR_MASTERY_THRESHOLD;

      return {
        goal,
        student,
        studentName: displayName(student),
        gradeLabel: student?.gradeLevel || student?.grade || null,
        domain: goal.goalArea || 'other',
        domainLabel:
          DOMAIN_LABELS[goal.goalArea] || DOMAIN_LABELS.other,
        domainIcon:
          DOMAIN_ICONS[goal.goalArea] || DOMAIN_ICONS.other,
        progress,
        days,
        status: goal.status || 'in_progress',
        isMastered,
        isDiscontinued,
        isActive,
        needsAttention,
        nearMastery,
        linkedIncidentCount: linkedIncidentCountByGoal.get(goal.id) || 0,
      };
    });
  }, [goals, studentById, linkedIncidentCountByGoal]);

  // Rollup tiles above the table. Computed off the unfiltered row list
  // so the numbers reflect the whole caseload, not just what's on
  // screen — a teacher wants "how many of my goals need attention" not
  // "how many of my filter's goals need attention".
  const summary = useMemo(() => {
    const active = rows.filter((r) => r.isActive);
    return {
      activeGoals: active.length,
      needsAttention: active.filter((r) => r.needsAttention).length,
      nearMastery: active.filter((r) => r.nearMastery).length,
      loggedThisWeek: active.filter(
        (r) => r.days !== null && r.days <= 7,
      ).length,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (!filters.showMastered && r.isMastered) return false;
        if (r.isDiscontinued && !filters.showMastered) return false;
        if (filters.domain !== 'all' && r.domain !== filters.domain) {
          return false;
        }
        if (q) {
          const hay = [
            r.studentName,
            r.goal.specific,
            r.goal.measurable,
            r.goal.target,
            r.domainLabel,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        switch (filters.sortBy) {
          case 'needs_attention': {
            // Needs-attention first; within that, most-stale (largest
            // day count / null-days) first. Then by student name so
            // rows for the same student cluster together.
            if (a.needsAttention !== b.needsAttention) {
              return a.needsAttention ? -1 : 1;
            }
            const aDays = a.days === null ? Infinity : a.days;
            const bDays = b.days === null ? Infinity : b.days;
            if (aDays !== bDays) return bDays - aDays;
            return a.studentName.localeCompare(b.studentName);
          }
          case 'progress_low': {
            const aP = a.progress === null ? -1 : a.progress;
            const bP = b.progress === null ? -1 : b.progress;
            return aP - bP;
          }
          case 'progress_high': {
            const aP = a.progress === null ? -1 : a.progress;
            const bP = b.progress === null ? -1 : b.progress;
            return bP - aP;
          }
          case 'recent':
            return (a.days ?? Infinity) - (b.days ?? Infinity);
          case 'student':
          default:
            return a.studentName.localeCompare(b.studentName);
        }
      });
  }, [rows, searchQuery, filters]);

  const domainOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(r.domain));
    return [
      { value: 'all', label: 'All domains' },
      ...Array.from(set)
        .sort()
        .map((d) => ({ value: d, label: DOMAIN_LABELS[d] || d })),
    ];
  }, [rows]);

  const sortOptions = [
    { value: 'needs_attention', label: 'Needs attention first' },
    { value: 'progress_low',    label: 'Progress: low → high' },
    { value: 'progress_high',   label: 'Progress: high → low' },
    { value: 'recent',          label: 'Most recently updated' },
    { value: 'student',         label: 'Student A → Z' },
  ];

  const handleLogData = (row) => {
    // Deep-link into the IEP Goals page with the goal already open in
    // the detail view — the goal detail modal is where the log-data
    // form lives, so this is the fastest path to "collect a probe on
    // this goal right now." Reuses the same navigation state that the
    // Collect Data launcher fires.
    navigate('/iep-goals-and-objectives-tracking', {
      state: { openGoalId: row.goal.id, studentId: row.student?.id },
    });
  };

  const handleOpenStudent = (row) => {
    if (!row.student?.id) return;
    navigate(`/student-profile-management/${row.student.id}`);
  };

  const loading = studentsLoading || goalsLoading;
  const hasStudents = (students || []).length > 0;
  const hasAnyGoals = (goals || []).length > 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6">
          <Breadcrumb />

          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Progress Tracker
              </h1>
              <p className="text-muted-foreground max-w-3xl">
                One view of every student's active IEP goals. See who needs
                data collected this week, spot goals falling behind, and log
                a data point in one tap.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/paper-trackers')}
              className="self-start min-h-12"
              title="Print a blank paper tracker to fill in by hand"
            >
              <Icon name="Printer" size={16} />
              Prefer paper? Print a sheet
            </Button>
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs font-medium text-muted-foreground">
                Active goals
              </div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {summary.activeGoals}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Across your caseload
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs font-medium text-muted-foreground">
                Data logged this week
              </div>
              <div className="text-2xl font-bold text-primary mt-1">
                {summary.loggedThisWeek}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Goals touched in the last 7 days
              </p>
            </div>
            <div
              className={`rounded-lg p-4 border ${
                summary.needsAttention > 0
                  ? 'bg-amber-50 border-amber-300'
                  : 'bg-card border-border'
              }`}
            >
              <div
                className={`text-xs font-medium ${
                  summary.needsAttention > 0
                    ? 'text-amber-800'
                    : 'text-muted-foreground'
                }`}
              >
                Needs attention
              </div>
              <div
                className={`text-2xl font-bold mt-1 ${
                  summary.needsAttention > 0
                    ? 'text-amber-900'
                    : 'text-foreground'
                }`}
              >
                {summary.needsAttention}
              </div>
              <p
                className={`text-xs mt-1 ${
                  summary.needsAttention > 0
                    ? 'text-amber-800'
                    : 'text-muted-foreground'
                }`}
              >
                No data in {STALE_THRESHOLD_DAYS}+ days
              </p>
            </div>
            <div className="bg-green-50 border border-green-300 rounded-lg p-4">
              <div className="text-xs font-medium text-green-800">
                Near mastery
              </div>
              <div className="text-2xl font-bold text-green-900 mt-1">
                {summary.nearMastery}
              </div>
              <p className="text-xs text-green-800 mt-1">
                Progress ≥ {NEAR_MASTERY_THRESHOLD}%
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                type="search"
                placeholder="Search student or goal…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value || '')}
              />
              <Select
                options={domainOptions}
                value={filters.domain}
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, domain: v }))
                }
                placeholder="Filter by domain"
              />
              <Select
                options={sortOptions}
                value={filters.sortBy}
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, sortBy: v }))
                }
                placeholder="Sort by"
              />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showMastered}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    showMastered: e.target.checked,
                  }))
                }
                className="rounded border-border"
              />
              Also show mastered / discontinued goals
            </label>
          </div>

          {/* Table / list */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon
                name="Loader2"
                size={32}
                className="animate-spin mx-auto mb-2"
              />
              Loading your caseload…
            </div>
          ) : !hasStudents ? (
            <EmptyPanel
              icon="Users"
              title="No students yet"
              body="Add students to your caseload to start tracking their goals."
              cta={{ label: 'Add a student', path: '/student-profile-management' }}
              onNavigate={navigate}
            />
          ) : !hasAnyGoals ? (
            <EmptyPanel
              icon="Target"
              title="No IEP goals yet"
              body="Once your students have goals, this tracker fills in automatically."
              cta={{
                label: 'Write your first goal',
                path: '/iep-goals-and-objectives-tracking',
              }}
              onNavigate={navigate}
            />
          ) : filteredRows.length === 0 ? (
            <EmptyPanel
              icon="Filter"
              title="No goals match"
              body="Try adjusting your search or clearing filters."
              cta={{
                label: 'Clear filters',
                onClick: () => {
                  setSearchQuery('');
                  setFilters({
                    domain: 'all',
                    sortBy: 'needs_attention',
                    showMastered: false,
                  });
                },
              }}
              onNavigate={navigate}
            />
          ) : (
            <TrackerTable
              rows={filteredRows}
              onLogData={handleLogData}
              onOpenStudent={handleOpenStudent}
            />
          )}
        </div>
      </main>
    </div>
  );
};

const EmptyPanel = ({ icon, title, body, cta, onNavigate }) => (
  <div className="text-center py-12 bg-card border border-border rounded-lg">
    <Icon
      name={icon}
      size={56}
      className="mx-auto mb-4 text-muted-foreground opacity-50"
    />
    <h3 className="text-lg font-semibold mb-1">{title}</h3>
    <p className="text-muted-foreground mb-4 max-w-md mx-auto">{body}</p>
    {cta && (
      <Button
        onClick={() => (cta.onClick ? cta.onClick() : onNavigate(cta.path))}
      >
        {cta.label}
      </Button>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// TrackerTable
//
// The core caseload table. Renders as a real HTML table on md+ screens
// (best for scanning columns) and as a stack of cards on phones. Every
// row shows: student, goal, domain pill, progress bar, days-since,
// stale badge, linked-incident count, "+ Log data" button.
// ---------------------------------------------------------------------------
const TrackerTable = ({ rows, onLogData, onOpenStudent }) => {
  return (
    <div>
      {/* Desktop / tablet — real table */}
      <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Student</th>
              <th className="px-4 py-3 font-medium">Goal</th>
              <th className="px-4 py-3 font-medium w-40">Progress</th>
              <th className="px-4 py-3 font-medium w-36">Last data</th>
              <th className="px-4 py-3 font-medium w-28 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <TrackerRowDesktop
                key={row.goal.id}
                row={row}
                onLogData={onLogData}
                onOpenStudent={onOpenStudent}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — cards */}
      <div className="md:hidden space-y-3">
        {rows.map((row) => (
          <TrackerRowMobile
            key={row.goal.id}
            row={row}
            onLogData={onLogData}
            onOpenStudent={onOpenStudent}
          />
        ))}
      </div>
    </div>
  );
};

const TrackerRowDesktop = ({ row, onLogData, onOpenStudent }) => {
  return (
    <tr
      className={`hover:bg-muted/20 transition-colors ${
        row.needsAttention ? 'bg-amber-50/50' : ''
      }`}
    >
      <td className="px-4 py-3 align-top">
        <button
          type="button"
          onClick={() => onOpenStudent(row)}
          className="text-left group"
        >
          <div className="font-semibold text-foreground group-hover:underline">
            {row.studentName}
          </div>
          {row.gradeLabel && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {row.gradeLabel}
            </div>
          )}
        </button>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            <Icon name={row.domainIcon} size={12} />
            {row.domainLabel}
          </span>
          {!row.isActive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {STATUS_LABEL[row.status]}
            </span>
          )}
          {row.linkedIncidentCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200">
              <Icon name="Link" size={11} />
              {row.linkedIncidentCount} incident
              {row.linkedIncidentCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="text-sm text-foreground line-clamp-2 max-w-xl">
          {row.goal.specific || row.goal.measurable || 'Untitled goal'}
        </p>
      </td>
      <td className="px-4 py-3 align-top">
        <ProgressCell row={row} />
      </td>
      <td className="px-4 py-3 align-top">
        <DataCell row={row} />
      </td>
      <td className="px-4 py-3 align-top text-right">
        <Button size="sm" onClick={() => onLogData(row)}>
          <Icon name="Plus" size={14} className="mr-1" />
          Log data
        </Button>
      </td>
    </tr>
  );
};

const TrackerRowMobile = ({ row, onLogData, onOpenStudent }) => {
  return (
    <div
      className={`rounded-lg border p-3 ${
        row.needsAttention
          ? 'bg-amber-50/50 border-amber-300'
          : 'bg-card border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <button
          type="button"
          onClick={() => onOpenStudent(row)}
          className="text-left flex-1 min-w-0"
        >
          <div className="font-semibold text-foreground truncate">
            {row.studentName}
          </div>
          {row.gradeLabel && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {row.gradeLabel}
            </div>
          )}
        </button>
        <Button size="sm" onClick={() => onLogData(row)}>
          <Icon name="Plus" size={14} className="mr-1" />
          Log
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          <Icon name={row.domainIcon} size={12} />
          {row.domainLabel}
        </span>
        {!row.isActive && (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {STATUS_LABEL[row.status]}
          </span>
        )}
        {row.linkedIncidentCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200">
            <Icon name="Link" size={11} />
            {row.linkedIncidentCount}
          </span>
        )}
      </div>
      <p className="text-sm text-foreground line-clamp-2 mb-3">
        {row.goal.specific || row.goal.measurable || 'Untitled goal'}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <ProgressCell row={row} />
        <DataCell row={row} />
      </div>
    </div>
  );
};

const ProgressCell = ({ row }) => {
  const pct = row.progress;
  const barPct = pct === null ? 0 : Math.max(0, Math.min(100, pct));
  const label = pct === null ? 'No data' : `${Math.round(pct)}%`;
  const barColor =
    pct === null
      ? 'bg-muted'
      : pct >= NEAR_MASTERY_THRESHOLD
        ? 'bg-green-500'
        : pct >= 40
          ? 'bg-primary'
          : 'bg-amber-500';
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">
          Progress
        </span>
        <span
          className={`text-sm font-semibold ${
            pct === null ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          {label}
        </span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${barPct}%` }}
        />
      </div>
    </div>
  );
};

const DataCell = ({ row }) => {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1">
        Last data
      </div>
      <div className="flex items-center gap-1.5">
        {row.needsAttention && (
          <Icon name="AlertTriangle" size={14} className="text-amber-600" />
        )}
        <span
          className={`text-sm ${
            row.needsAttention
              ? 'font-semibold text-amber-800'
              : 'text-foreground'
          }`}
        >
          {formatDaysAgo(row.days)}
        </span>
      </div>
    </div>
  );
};

export default StudentProgressTracker;
